// The legal notices at `/legal/<notice>`, for anyone, signed in or not (M3-29c): the Privacy Notice, the contact and
// grievances page and the notice template for Tenants, as the Cell serves them from one place
// (`/api/legal/<notice>`). A person whose language is Hindi reads the Hindi edition, with a link to the English
// one; each shows its version. A production Cell serves only reviewed notices; in a development Cell a notice not
// yet lawyer-reviewed shows a DRAFT banner. `LegalLinks` puts the notices at the foot of the Cockpit and the
// public pages.
import { useEffect, useState, type ReactNode } from "react";
import type { Language } from "../i18n/language.js";
import { isLanguage } from "../i18n/language.js";
import { useLanguage, useWords } from "../i18n/words.js";

export const LEGAL_PATH = "/legal/";
export const NOTICES = ["privacy-notice", "contact-and-grievances", "tenant-notice-template"] as const;
export type Notice = (typeof NOTICES)[number];

/** Where an edition of a notice is shown; the api answers the same path under /api. */
export const noticeHref = (notice: Notice, language: Language): string => `${LEGAL_PATH}${notice}?language=${language}`;

/** The notice a path shows, if it is one. */
export function noticeAt(path: string): Notice | undefined {
  const name = path.startsWith(LEGAL_PATH) ? path.slice(LEGAL_PATH.length).replace(/\/$/, "") : "";
  return (NOTICES as readonly string[]).includes(name) ? (name as Notice) : undefined;
}

/** What the api answers for a notice. */
export interface ServedNotice {
  readonly notice: Notice;
  readonly language: Language;
  readonly version: string;
  readonly review: "draft" | "translator-reviewed" | "lawyer-reviewed";
  readonly draft: boolean;
  readonly text: string;
  readonly english: { readonly href: string; readonly version: string } | null;
}

export type LoadNotice = (notice: Notice, language: Language) => Promise<ServedNotice | undefined>;

export const loadNotice: LoadNotice = async (notice, language) => {
  const response = await fetch(`/api${noticeHref(notice, language)}`, { credentials: "omit" });
  return response.ok ? ((await response.json()) as ServedNotice) : undefined;
};

export type NoticeBlock =
  | { readonly kind: "heading"; readonly level: 1 | 2 | 3; readonly text: string }
  | { readonly kind: "list"; readonly items: readonly string[] }
  | { readonly kind: "table"; readonly rows: readonly (readonly string[])[] }
  | { readonly kind: "rule" }
  | { readonly kind: "paragraph"; readonly text: string };

/** A link to another notice's file, as the drafts write it, becomes a link to that notice's page. */
const NOTICE_FILE = /^([a-z-]+?)(\.hi)?\.md$/;

export function noticeLink(href: string): string | undefined {
  const match = NOTICE_FILE.exec(href);
  const notice = match?.[1];
  if (!notice || !(NOTICES as readonly string[]).includes(notice)) return undefined;
  return noticeHref(notice as Notice, match[2] ? "hi" : "en");
}

/** A notice's Markdown, as the blocks the page shows: headings, lists, tables, rules and paragraphs. */
export function noticeBlocks(markdown: string): NoticeBlock[] {
  const blocks: NoticeBlock[] = [];
  for (const chunk of markdown.split(/\n\s*\n/)) {
    const lines = chunk
      .split("\n")
      .map((line) => line.replace(/^>\s?/, "").trim())
      .filter((line) => line.length > 0);
    const [first] = lines;
    if (first === undefined) continue;
    if (/^-{3,}$/.test(first) && lines.length === 1) blocks.push({ kind: "rule" });
    else if (/^#{1,6}\s/.test(first)) {
      const level = Math.min(first.indexOf(" "), 3) as 1 | 2 | 3;
      blocks.push({ kind: "heading", level, text: first.replace(/^#+\s+/, "") });
      if (lines.length > 1) blocks.push({ kind: "paragraph", text: lines.slice(1).join(" ") });
    } else if (lines.every((line) => line.startsWith("|")))
      blocks.push({
        kind: "table",
        rows: lines
          .filter((line) => !/^\|[\s|:-]+\|$/.test(line))
          .map((line) =>
            line
              .replace(/^\||\|$/g, "")
              .split("|")
              .map((cell) => cell.trim()),
          ),
      });
    else if (lines.every((line) => /^(?:[-*]|\d+\.)\s/.test(line)))
      blocks.push({ kind: "list", items: lines.map((line) => line.replace(/^(?:[-*]|\d+\.)\s+/, "")) });
    else blocks.push({ kind: "paragraph", text: lines.join(" ") });
  }
  return blocks;
}

/** Bold and links in a line of a notice; links to other notices go to their pages, other files are named only. */
function Inline({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let at = 0;
  for (const match of text.matchAll(pattern)) {
    parts.push(text.slice(at, match.index));
    if (match[1] !== undefined) parts.push(<strong key={match.index}>{match[1]}</strong>);
    else {
      const label = match[2] ?? "";
      const href = match[3] ?? "";
      const to = noticeLink(href) ?? (/^(?:https:|mailto:)/.test(href) ? href : undefined);
      parts.push(
        to ? (
          <a key={match.index} href={to}>
            {label}
          </a>
        ) : (
          label
        ),
      );
    }
    at = match.index + match[0].length;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

function Blocks({ blocks }: { blocks: readonly NoticeBlock[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case "heading":
            return block.level === 1 ? (
              <h1 key={i}>
                <Inline text={block.text} />
              </h1>
            ) : block.level === 2 ? (
              <h2 key={i} className="sectionh">
                <Inline text={block.text} />
              </h2>
            ) : (
              <h3 key={i}>
                <Inline text={block.text} />
              </h3>
            );
          case "list":
            return (
              <ul key={i}>
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Inline text={item} />
                  </li>
                ))}
              </ul>
            );
          case "table":
            return (
              <div key={i} className="tbl-wrap">
                <table className="tbl">
                  <tbody>
                    {block.rows.map((row, j) => (
                      <tr key={j}>
                        {row.map((cell, k) => (
                          <td key={k}>
                            <Inline text={cell} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "rule":
            return <hr key={i} />;
          case "paragraph":
            return (
              <p key={i}>
                <Inline text={block.text} />
              </p>
            );
        }
      })}
    </>
  );
}

/** The notice at `/legal/<notice>`: in `?language=` when the link names one, else in the reader's language. */
export function LegalNotice({ notice, load = loadNotice }: { notice: Notice; load?: LoadNotice }) {
  const w = useWords();
  const { language: reading } = useLanguage();
  const asked = new URLSearchParams(window.location.search).get("language");
  const language = isLanguage(asked) ? asked : reading;
  const [served, setServed] = useState<ServedNotice | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setServed(undefined);
    load(notice, language).then(
      (n) => live && setServed(n ?? null),
      () => live && setServed(null),
    );
    return () => {
      live = false;
    };
  }, [load, notice, language]);

  if (served === undefined)
    return (
      <p role="status" className="loading">
        {w.loading}
      </p>
    );
  if (served === null)
    return (
      <main id="content" className="public-page">
        <h1>{w.legalUnavailableTitle}</h1>
        <p>{w.legalUnavailable}</p>
        <p>
          <a href="/">{w.trustBack}</a>
        </p>
      </main>
    );
  return (
    <main id="content" className="public-page legal-notice" lang={served.language}>
      {served.draft ? (
        <p role="note" className="trust-draft">
          <strong>{w.trustDraft}</strong> {w.legalDraftBody}
        </p>
      ) : null}
      <p className="small muted">{w.legalVersion(served.version)}</p>
      {served.english ? (
        <p>
          <a href={served.english.href} lang="en">
            {w.legalReadInEnglish}
          </a>
        </p>
      ) : null}
      <Blocks blocks={noticeBlocks(served.text)} />
    </main>
  );
}

/** The notices at the foot of the Cockpit and the public pages, in the reader's language. */
export function LegalLinks() {
  const w = useWords();
  const { language } = useLanguage();
  return (
    <footer className="legal-links small muted">
      <a href={noticeHref("privacy-notice", language)}>{w.legalPrivacyNotice}</a>
      {" · "}
      <a href={noticeHref("contact-and-grievances", language)}>{w.legalContactAndGrievances}</a>
    </footer>
  );
}
